import axiosInstance from "./apiinterseptor"; // Assuming same interceptor setup
import { toast } from "react-toastify";
import axios, { AxiosResponse } from 'axios';
import { ICreateTallentPoolFormData } from "../pages/PublicTalentPoolForm";

// Interface for location details
export interface ILocationDetails {
  street: string;
  area: string;
  city: string; // Required field
  pincode: string;
  fullAddress: string;
}

// Interface for user details
interface IUserDetails {
  id: string;
  username: string;
  email: string;
}

// Interface for candidate data
interface ICandidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  highestQualification: string;
  totalExperienceYears: number;
  totalExperienceMonths: number;
  skills: string[];
  currentLocationDetails: ILocationDetails;
  profileSource: string;
  createdAt: string;
  candidateProfileStrength: number;
  resumeUrl?: string;
}

// Interface for job details
interface IJob {
  id: string;
  title: string;
  companyName: string;
}

// Interface for application status
interface ICurrentStatusInfo {
  status: string;
  timestamp: Date;
  updatedBy: IUserDetails | null;
}

// Interface for status history
interface IStatusHistory {
  status: string;
  changedAt: Date;
  comments: string;
  changedBy: IUserDetails | null;
}

// Interface for payout details
export interface IPayoutDetails {
    employeeIdAssignedByCompany?: string;
  finalPositionName?: string;
  finalSalary?: number;
  commissionReceivableFromCompany?: number;
  commissionPayableToSourcer?: number;
  isSourcerCommissionClaimed?: boolean;
  claimedByUserId?: string;
  claimDate?: Date;
  [key: string]: any; // For additional payout fields
}

// Interface for application details
interface IApplicationDetails {
  id: string;
  currentStatus: ICurrentStatusInfo;
  candidate: ICandidate;
  job: IJob;
  sourcedBy: IUserDetails | null;
  appliedOn: Date;
  interviewId?: string;
  verificationStatus?: string;
  payoutDetails?: IPayoutDetails;
  statusHistory: IStatusHistory[];
}
interface ICandidateDetails {
  id: string;
  name: string;
  email: string;
  phone: string;
  availabilityStatus: string;
  highestQualification: string;
  totalExperienceYears: number;
  skills: string[];
  currentLocation: string;
  sourcedBy: {
      id: string;
      username: string;
      email: string;
  },
  profileCreatedAt: string
}

// Interface for application history item (used in getApplicationsByCandidateId)
interface IApplicationHistoryItem {
  applicationId: string;
  job: IJob;
  currentStatus: ICurrentStatusInfo;
  appliedOn: Date;
  sourcedBy: IUserDetails | null;
}

// Make sure to export the interface
export interface ICreateApplicationFormData {
  // --- Job Details (Required) ---
  jobId: string;
  jobType: 'main' | 'sub';

  // --- Candidate Details (All required as per backend schema) ---
  name: string;
  email: string;
  phone: string;
  alternativePhoneNumber?: string;
  maritalStatus?: string;
  howOftenAbleToJoin?: string;
  fatherName: string;
  dateOfBirth: string; // ISO 8601 date string
  gender: 'Male' | 'Female' | 'Other';
  aadharNumber?: string;
  highestQualification: string;
  currentLocationDetails: ILocationDetails;
  spokenLanguages: string[];
  totalExperienceYears: number;
  totalExperienceMonths: number;
  skills: string[];
  knownTools?: string[];
  preferredJobCategories: string[];
  preferredEmploymentTypes: string[];
  preferredWorkTypes: ('remote' | 'hybrid' | 'on-site')[];
  scheduleInterview: boolean;
  sourceMechanism?: 'internal_upload' | 'approaches' | 'hot_leads';
  // File upload
  resume?: File;
  resumeUrl?: string;
}

// Interface for get applications response
interface IGetApplicationsResponse {
  status: string;
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
  results: number;
  data: {
    applications: IApplicationDetails[];
  };
}

// Interface for create application response
interface ICreateApplicationResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    candidateId: string;
    jobId: string;
    currentStatus: string;
    isNewCandidate: boolean;
    interviewId: string; // New field to return the interview ID if scheduled
    roundId: string; // New field to return the round ID if scheduled
  };
}

// Interface for update application status response
interface IUpdateApplicationStatusResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    newStatus: string;
  };
}

// Interface for update verification status response
interface IUpdateVerificationStatusResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    verificationStatus: string;
  };
}

// Interface for update tenure status response
interface IUpdateTenureStatusResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    newStatus: string;
  };
}

// Enum for tenure eligibility status
export enum TenureEligibilityStatus {
  ELIGIBLE = 'Eligible',
  NOT_ELIGIBLE = 'Not Eligible',
  UNKNOWN = 'Unknown',
}

// Interface for update tenure eligibility status response
interface IUpdateTenureEligibilityStatusResponse {
  message: string;
}

// Interface for get application by ID response
interface IGetApplicationByIdResponse {
  status: string;
  data: {
    application: IApplicationDetails;
  };
}

// Interface for edit application response
interface IEditApplicationResponse {
  status: string;
  message: string;
  data: {
    application: IApplicationDetails;
  };
}

// Interface for payout summary
interface IPayoutSummary {
  [key: string]: any; // Adjust based on actual summary structure
}

// Interface for get payout applications response
interface IGetPayoutApplicationsResponse {
  status: string;
  totalPages: number;
  totalResults: number;
   page?: number;
  limit?: number;
  _t?: number;
  search?: string;
  jobId?: string;
  recruiterId?: string;
  joiningDateFrom?: string;
  joiningDateTo?: string;
  results: number;
  summary: IPayoutSummary;
  data: {
    applications: IApplicationDetails[];
  };
}



// Interface for update payout details response
interface IUpdatePayoutDetailsResponse {
  status: string;
  message: string;
  data: {
    application: IApplicationDetails;
  };
}

// Interface for update claim status response
interface IUpdateClaimStatusResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    payoutDetails: IPayoutDetails;
  };
}

// Interface for get joined applications response
interface IGetJoinedApplicationsResponse {
  status: string;
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
  results: number;
  summary: IPayoutSummary;
  data: {
    applications: IApplicationDetails[];
  };
}

// Interface for get applications by candidate ID response
interface IGetApplicationsByCandidateResponse {
  status: string;
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
  results: number;
  data: {
    applications: IApplicationHistoryItem[];
  };
}

// Get public job applications by jobId
export const getPublicJobApplicationsByJobId = async (
  jobId: string,
  params: {
    page?: number;
    limit?: number;
    search?: string;
    _t?: number; // For cache busting
  } = {}
): Promise<IGetApplicationsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params._t) queryParams.append('_t', params._t.toString());

    const apiUrl = `/public/job-applications/${jobId}?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching public job applications by jobId');
    console.log('JobId:', jobId);
    console.log('Request params:', params);
    console.log('Query params string:', queryParams.toString());
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Public job applications fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch public job applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view applications.');
    } else if (error.response?.status === 404) {
      toast.error('No applications found or endpoint not available.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch applications. Please try again.');
    }
    throw error;
  }
};

// Get all applications
export const getAllApplications = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  jobId?: string;
  applicationStatus?: string;
  recruiterId?: string;
  interviewDateFrom?: string;
  interviewDateTo?: string;
  _t?: number; // For cache busting
} = {}): Promise<IGetApplicationsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.jobId) queryParams.append('jobId', params.jobId);
    if (params.applicationStatus) queryParams.append('applicationStatus', params.applicationStatus);
    if (params.recruiterId) queryParams.append('recruiterId', params.recruiterId);
    if (params.interviewDateFrom) queryParams.append('interviewDateFrom', params.interviewDateFrom);
    if (params.interviewDateTo) queryParams.append('interviewDateTo', params.interviewDateTo);
    if (params._t) queryParams.append('_t', params._t.toString());

    const apiUrl = `/applications?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching applications');
    console.log('Request params:', params);
    console.log('Query params string:', queryParams.toString());
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Applications fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view applications.');
    } else if (error.response?.status === 404) {
      toast.error('No applications found or endpoint not available.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch applications. Please try again.');
    }
    throw error;
  }
};


export const getCoordinatedinterviews = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  jobId?: string;
  companyName?: string;
  applicationStatus?: string;
  recruiterId?: string;
  interviewDateFrom?: string;
  interviewDateTo?: string;
  userId?: string;
  _t?: number; // For cache busting
} = {}): Promise<IGetApplicationsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.jobId) queryParams.append('jobId', params.jobId);
    if (params.companyName) queryParams.append('companyName', params.companyName);
    if (params.applicationStatus) queryParams.append('applicationStatus', params.applicationStatus);
    if (params.recruiterId) queryParams.append('recruiterId', params.recruiterId);
    if (params.interviewDateFrom) queryParams.append('interviewDateFrom', params.interviewDateFrom);
    if (params.interviewDateTo) queryParams.append('interviewDateTo', params.interviewDateTo);
    if (params._t) queryParams.append('_t', params._t.toString());
    if (params.userId) queryParams.append('userId', params.userId);

    const apiUrl = `applications/coordinated-interviews?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching coordinated interviews');
    console.log('Request params:', params);
    console.log('Query params string:', queryParams.toString());
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Applications fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view applications.');
    } else if (error.response?.status === 404) {
      toast.error('No applications found or endpoint not available.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch applications. Please try again.');
    }
    throw error;
  }
};


export interface CandidateAvailabilityStatus {
  type: 'Available' | 'Placed' | 'Do Not Contact';
}
export interface IGetCandidatesQuery {
  page?: Number;
  limit?: Number;
  search?: string; // For name, email, phone
  skills?: string; // Comma-separated string, e.g., "React,Node.js"
  minExperience?: string;
  location?: string; // Search by city
  sourcedBy?: string; // Filter by the ID of the recruiter who created the profile
  availabilityStatus?: CandidateAvailabilityStatus;
  status: string;
  
  totalPages: number;
  totalResults: number;
  results: number;
   data: {
    candidates: ICandidateDetails[];
  };
  
}

export const getAllCandidates = async (params: {
  page?: Number;
  limit?: Number;
  search?: string; // For name, email, phone
  skills?: string; // Comma-separated string, e.g., "React,Node.js"
  minExperience?: string;
  location?: string; // Search by city
  sourcedBy?: string; // Filter by t  he ID of the recruiter who created the profile
  availabilityStatus?: CandidateAvailabilityStatus;
 
} = {}): Promise<IGetCandidatesQuery> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.skills) queryParams.append('skills', params.skills);
    if (params.minExperience) queryParams.append('minExperience', params.minExperience);
    if (params.location) queryParams.append('location', params.location);
    if (params.sourcedBy) queryParams.append('sourcedBy', params.sourcedBy);
    if (params.availabilityStatus) queryParams.append('availabilityStatus', JSON.stringify(params.availabilityStatus));

    const apiUrl = `/candidates?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching applications');
    console.log('API URL:', fullUrl);
    console.log('Request params:', params);

    const response = await axiosInstance.get(apiUrl);

    console.log('Applications fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view applications.');
    } else if (error.response?.status === 404) {
      toast.error('No applications found or endpoint not available.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch applications. Please try again.');
    }
    throw error;
  }
};

// Create a new application
export const createApplication = async (
  data: ICreateApplicationFormData,
  resumeFile?: File
): Promise<ICreateApplicationResponse> => {
  try {
    const apiUrl = '/applications';
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Creating new application');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    // Validate required fields according to backend schema
    const requiredFields = [
      'name', 'email', 'phone', 'fatherName', 'dateOfBirth', 
      'gender', 'highestQualification', 'jobId', 'jobType'
    ];
    
    for (const field of requiredFields) {
      if (!data[field as keyof ICreateApplicationFormData]) {
        throw new Error(`${field} is a required field`);
      }
    }

    // Validate currentLocationDetails.city is required
    if (!data.currentLocationDetails?.city) {
      throw new Error('City is required in location details');
    }

    // Ensure all array fields are properly formatted
    const normalizedData = {
      jobId: data.jobId,
      jobType: data.jobType,
      name: data.name,
      email: data.email,
      phone: data.phone,
      fatherName: data.fatherName,
      dateOfBirth: data.dateOfBirth,
      gender: data.gender,
      ...(data.aadharNumber && data.aadharNumber.trim() && { aadharNumber: data.aadharNumber }),
      ...(data.alternativePhoneNumber && data.alternativePhoneNumber.trim() && { alternativePhoneNumber: data.alternativePhoneNumber }),
      ...(data.maritalStatus && data.maritalStatus.trim() && { maritalStatus: data.maritalStatus }),
      ...(data.howOftenAbleToJoin && data.howOftenAbleToJoin.trim() && { howOftenAbleToJoin: data.howOftenAbleToJoin }),
      highestQualification: data.highestQualification,
      currentLocationDetails: data.currentLocationDetails,
      totalExperienceYears: data.totalExperienceYears,
      totalExperienceMonths: data.totalExperienceMonths,
      // Ensure arrays have at least one element and are properly formatted
      skills: Array.isArray(data.skills) && data.skills.length > 0 ? data.skills : ["General"],
      knownTools: Array.isArray(data.knownTools) && data.knownTools.length > 0 ? data.knownTools : ["General"],
      spokenLanguages: Array.isArray(data.spokenLanguages) && data.spokenLanguages.length > 0 ? data.spokenLanguages : ["English"],
      preferredJobCategories: Array.isArray(data.preferredJobCategories) && data.preferredJobCategories.length > 0 ? data.preferredJobCategories : ["General"],
      preferredEmploymentTypes: Array.isArray(data.preferredEmploymentTypes) && data.preferredEmploymentTypes.length > 0 ? data.preferredEmploymentTypes : ["Full-time"],
      preferredWorkTypes: Array.isArray(data.preferredWorkTypes) && data.preferredWorkTypes.length > 0 ? data.preferredWorkTypes : ["on-site"],
      ...(data.sourceMechanism && { sourceMechanism: data.sourceMechanism }),
      ...(data.resumeUrl && data.resumeUrl.trim() && { resumeUrl: data.resumeUrl }),
      ...(data.scheduleInterview !== undefined && { scheduleInterview: data.scheduleInterview })
    };

    console.log('Normalized data to send:', normalizedData);

    // Send as JSON instead of FormData
    const response = await axiosInstance.post(apiUrl, normalizedData, {
      headers: { 
        'Content-Type': 'application/json'
      },
    });

    console.log('Application created successfully:', response.data);
    toast.success('Application submitted successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to create application:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || 'Invalid application data. Please check the provided information.';
      toast.error(errorMessage);
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        '';
      // Blocked-phone is shown as a popup on Add Candidate; skip toast there.
      if (!/blocked by admin/i.test(msg)) {
        toast.error('Access denied. You may not have permission to create applications.');
      }
    } else if (error.response?.status === 409) {
      toast.error('This candidate has already been applied to this job.');
    } else if (error.response?.status === 422) {
      const errorMessage = error.response?.data?.message || 'Validation error. Please check the application data.';
      toast.error(errorMessage);
    } else {
      toast.error(error.response?.data?.message || 'Failed to create application. Please try again.');
    }
    throw error;
  }
};

export const gettalentPoolCandidates = async (params: {
  page?: Number;
  limit?: Number;
  search?: string; // For name, email, phone
  skills?: string; // Comma-separated string, e.g., "React,Node.js"
  minExperience?: string;
  location?: string; // Search by city
  sourcedBy?: string; // legacy alias (maps to recruiterId)
  recruiterId?: string; // Filter by the ID of the recruiter who created the profile
  applicationStatus?: 'all' | 'shortlisted' | 'rejected' | 'no_action';
  availabilityStatus?: CandidateAvailabilityStatus;
  dateFrom?: string; // Filter by creation date from
  dateTo?: string;
  category?: string; // Filter by category
 
} = {}):Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.skills) queryParams.append('skills', params.skills);
    if (params.minExperience) queryParams.append('minExperience', params.minExperience);
    if (params.location) queryParams.append('location', params.location);
    const recruiterId = params.recruiterId || params.sourcedBy;
    if (recruiterId) queryParams.append('recruiterId', recruiterId);
    if (params.applicationStatus && params.applicationStatus !== 'all') {
      queryParams.append('applicationStatus', params.applicationStatus);
    }
    if (params.availabilityStatus) queryParams.append('availabilityStatus', JSON.stringify(params.availabilityStatus));
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.category) queryParams.append('category', params.category);

    const apiUrl = `/talentPoolCandidates?${queryParams.toString()}`;
    

    const response = await axiosInstance.get(apiUrl);

    console.log('Applications fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view applications.');
    } else if (error.response?.status === 404) {
      toast.error('No applications found or endpoint not available.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch applications. Please try again.');
    }
    throw error;
  }
};

export const getTalentPoolCandidateById = async (id: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(`/talentPoolCandidates/${id}`);
    return response.data;
  } 
  catch (error: any) {
    console.error('Failed to fetch applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view applications.');
    } else if (error.response?.status === 404) {
      // Handle 404 error
    }
    throw error;
  }
};

export const getJobCategoriesList = async (): Promise<any> => {
  try {
    const response = await axiosInstance.get('/jobs/categories');
    return response.data;
  }
  catch (error: any) {
    console.error('Failed to fetch job categories:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);
    throw error;
  }
};

export const getSkillsList = async (params: { search?: string; limit?: number } = {}): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search && params.search.trim()) queryParams.set("search", params.search.trim());
    if (params.limit) queryParams.set("limit", String(params.limit));
    const queryString = queryParams.toString();
    const response = await axiosInstance.get(`/jobs/skills${queryString ? `?${queryString}` : ""}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch skills list:', error.response?.data?.message || error.message);
    throw error;
  }
};

export const getToolsList = async (params: { search?: string; limit?: number } = {}): Promise<any> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search && params.search.trim()) queryParams.set("search", params.search.trim());
    if (params.limit) queryParams.set("limit", String(params.limit));
    const queryString = queryParams.toString();
    const response = await axiosInstance.get(`/jobs/tools${queryString ? `?${queryString}` : ""}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch tools list:', error.response?.data?.message || error.message);
    throw error;
  }
};


export const createTalentPoolcandidatePublic = async (
  id: string,
  data: ICreateTallentPoolFormData,
  resumeFile?: File
): Promise<ICreateApplicationResponse> => {
  try {

  

    // Validate required fields according to backend schema
    const requiredFields = [
      'name', 'email', 'phone', 'fatherName', 'dateOfBirth', 
      'gender', 'highestQualification', 
    ];
    
    for (const field of requiredFields) {
      if (!data[field as keyof ICreateTallentPoolFormData]) {
        throw new Error(`${field} is a required field`);
      }
    }

    // Validate currentLocationDetails.city is required
    if (!data.currentLocationDetails?.city) {
      throw new Error('City is required in location details');
    }

    // Ensure all array fields are properly formatted
    const normalizedData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      fatherName: data.fatherName,
      dateOfBirth: data.dateOfBirth,
      gender: data.gender,
      ...(data.aadharNumber && data.aadharNumber.trim() && { aadharNumber: data.aadharNumber }),
      highestQualification: data.highestQualification,
      currentLocationDetails: data.currentLocationDetails,
      totalExperienceYears: data.totalExperienceYears,
      totalExperienceMonths: data.totalExperienceMonths,
      skills: Array.isArray(data.skills) && data.skills.length > 0 ? data.skills : ["General"],
      spokenLanguages: Array.isArray(data.spokenLanguages) && data.spokenLanguages.length > 0 ? data.spokenLanguages : ["English"],
      preferredJobCategories: Array.isArray(data.preferredJobCategories) && data.preferredJobCategories.length > 0 ? data.preferredJobCategories : ["General"],
      preferredEmploymentTypes: Array.isArray(data.preferredEmploymentTypes) && data.preferredEmploymentTypes.length > 0 ? data.preferredEmploymentTypes : ["Full-time"],
      preferredWorkTypes: Array.isArray(data.preferredWorkTypes) && data.preferredWorkTypes.length > 0 ? data.preferredWorkTypes : ["on-site"],
      resumeUrl:data.resume
    };

    console.log('Normalized data to send:', normalizedData);

    // Send as JSON instead of FormData
    const response = await axiosInstance.post(`/talentPoolCandidates/${id}/createTalentPoolCandidate`, normalizedData, {
      headers: { 
        'Content-Type': 'application/json'
      },
    });

    console.log('Application created successfully:', response.data);
    toast.success('Application submitted successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to create application:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || 'Invalid application data. Please check the provided information.';
      toast.error(errorMessage);
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to create applications.');
    } else if (error.response?.status === 409) {
      toast.error('This candidate has already been applied to this job.');
    } else if (error.response?.status === 422) {
      const errorMessage = error.response?.data?.message || 'Validation error. Please check the application data.';
      toast.error(errorMessage);
    } else {
      toast.error(error.response?.data?.message || 'Failed to create application. Please try again.');
    }
    throw error;
  }
};


export const createTalentPoolCandidateInternal = async (
  data: ICreateTallentPoolFormData,
): Promise<ICreateApplicationResponse> => {
  try {

  

    // Validate required fields according to backend schema
    const requiredFields = [
      'name', 'email', 'phone', 'fatherName', 'dateOfBirth', 
      'gender', 'highestQualification', 
    ];
    
    for (const field of requiredFields) {
      if (!data[field as keyof ICreateTallentPoolFormData]) {
        throw new Error(`${field} is a required field`);
      }
    }

    // Validate currentLocationDetails.city is required
    if (!data.currentLocationDetails?.city) {
      throw new Error('City is required in location details');
    }

    // Ensure all array fields are properly formatted
    const normalizedData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      fatherName: data.fatherName,
      dateOfBirth: data.dateOfBirth,
      gender: data.gender,
      ...(data.aadharNumber && data.aadharNumber.trim() && { aadharNumber: data.aadharNumber }),
      highestQualification: data.highestQualification,
      ...(data.collegeName && data.collegeName.trim() ? { collegeName: data.collegeName.trim() } : {}),
      currentLocationDetails: data.currentLocationDetails,
      totalExperienceYears: data.totalExperienceYears,
      totalExperienceMonths: data.totalExperienceMonths,
      skills: Array.isArray(data.skills) && data.skills.length > 0 ? data.skills : ["General"],
      spokenLanguages: Array.isArray(data.spokenLanguages) && data.spokenLanguages.length > 0 ? data.spokenLanguages : ["English"],
      preferredJobCategories: Array.isArray(data.preferredJobCategories) && data.preferredJobCategories.length > 0 ? data.preferredJobCategories : ["General"],
      preferredEmploymentTypes: Array.isArray(data.preferredEmploymentTypes) && data.preferredEmploymentTypes.length > 0 ? data.preferredEmploymentTypes : ["Full-time"],
      preferredWorkTypes: Array.isArray(data.preferredWorkTypes) && data.preferredWorkTypes.length > 0 ? data.preferredWorkTypes : ["on-site"],
      resumeUrl:data.resume,
      howSoonReady:data.howSoonReady,
      preferredJobLocations:data.preferredJobLocations,
      expectedSalary:data.expectedSalary,

      };

    console.log('Normalized data to send:', normalizedData);

    // Send as JSON instead of FormData
    const response = await axiosInstance.post('/talentPoolCandidates/createTalentPoolCandidateInternal', normalizedData, {
      headers: { 
        'Content-Type': 'application/json'
      },
    });

    console.log('Application created successfully:', response.data);
    toast.success('Application submitted successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to create application:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || 'Invalid application data. Please check the provided information.';
      toast.error(errorMessage);
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to create applications.');
    } else if (error.response?.status === 409) {
      toast.error('This candidate has already been applied to this job.');
    } else if (error.response?.status === 422) {
      const errorMessage = error.response?.data?.message || 'Validation error. Please check the application data.';
      toast.error(errorMessage);
    } else {
      toast.error(error.response?.data?.message || 'Failed to create application. Please try again.');
    }
    throw error;
  }
};




// Update application status
export const updateApplicationStatus = async (
  applicationId: string,
  data: {
    newStatus: string;
    comments?: string;
    offeredDate?: string;
    joiningDate?: string;
  }
): Promise<IUpdateApplicationStatusResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/status`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Updating application status');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    const response = await axiosInstance.patch(apiUrl, data);

    console.log('Application status updated successfully:', response.data);
    toast.success('Application status updated successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to update application status:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error('Invalid status update data.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to update status.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update application status.');
    }
    throw error;
  }
};

// ADMIN + SHM: update candidate name / email / phone (audit trail on server)
export const updateCandidateContactDetails = async (
  candidateId: string,
  data: { name?: string; email?: string; phone?: string }
): Promise<{ status: string; message?: string; data?: { candidateId: string; newValues: Record<string, string> } }> => {
  try {
    if (!/^[0-9a-fA-F]{24}$/.test(candidateId)) {
      throw new Error('Invalid Candidate ID format.');
    }
    const apiUrl = `/candidates/${candidateId}/contact-details`;
    const response = await axiosInstance.patch(apiUrl, data);
    toast.success(response.data?.message || 'Candidate contact updated');
    return response.data;
  } catch (error: any) {
    const msg =
      error?.response?.data?.message || error?.message || 'Failed to update candidate contact';
    if (error?.response?.status === 403) {
      toast.error('You do not have permission to edit this candidate.');
    } else if (error?.response?.status === 409) {
      toast.error(msg);
    } else {
      toast.error(msg);
    }
    throw error;
  }
};

// Update verification status
export const updateVerificationStatus = async (
  applicationId: string,
  data: {
    verificationStatus: string;
    comments?: string;
  }
): Promise<IUpdateVerificationStatusResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/verification-status`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Updating verification status');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    const response = await axiosInstance.patch(apiUrl, data);

    console.log('Verification status updated successfully:', response.data);
    toast.success('Verification status updated successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to update verification status:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error('Invalid verification status data.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to update verification status.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update verification status.');
    }
    throw error;
  }
};

// Update tenure status
export const updateTenureStatus = async (
  applicationId: string,
  data: {
    isTenureApproved: boolean | null;
    comments?: string;
    lastWorkingDay?: string;
    payoutDetails?: IPayoutDetails;
  },
  options?: { silent?: boolean }
): Promise<IUpdateTenureStatusResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/tenure-status`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Updating tenure status');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    const response = await axiosInstance.patch(apiUrl, data);

    console.log('Tenure status updated successfully:', response.data);
    if (!options?.silent) {
      toast.success('Tenure status updated successfully');
    }
    return response.data;
  } catch (error: any) {
    console.error('Failed to update tenure status:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error('Invalid tenure status data.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to update tenure status.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update tenure status.');
    }
    throw error;
  }
};

// Update tenure eligibility status
export const updateTenureEligibilityStatus = async (
  applicationId: string,
  tenureEligibilityStatus: TenureEligibilityStatus
): Promise<IUpdateTenureEligibilityStatusResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    // Validate tenure eligibility status
    if (!Object.values(TenureEligibilityStatus).includes(tenureEligibilityStatus)) {
      throw new Error('Invalid tenure eligibility status value.');
    }

    const apiUrl = `/applications/${applicationId}/tenure-eligibility-status`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Updating tenure eligibility status');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', { tenureEligibilityStatus });

    const response = await axiosInstance.patch(apiUrl, { tenureEligibilityStatus });

    console.log('Tenure eligibility status updated successfully:', response.data);
    toast.success('Tenure eligibility status updated successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to update tenure eligibility status:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message;
      if (errorMessage?.includes('remaining')) {
        toast.error(`Tenure period not complete. ${errorMessage}`);
      } else if (errorMessage?.includes('Tenure duration is not defined')) {
        toast.error('Tenure duration is not defined for this job.');
      } else if (errorMessage?.includes('no joining date')) {
        toast.error('Candidate has no joining date. Cannot determine tenure.');
      } else {
        toast.error('Invalid tenure eligibility status data.');
      }
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to update tenure eligibility status.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update tenure eligibility status.');
    }
    throw error;
  }
};

// Get application by ID
export const getApplicationById = async (applicationId: string): Promise<IGetApplicationByIdResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching application by ID');
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Application fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch application:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view this application.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch application.');
    }
    throw error;
  }
};

// Edit application (ADMIN only)
export const editApplication = async (
  applicationId: string,
  data: {
    jobId: string;
    jobType: string;
    comments: string;
  }
): Promise<IEditApplicationResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Editing application');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    const response = await axiosInstance.patch(apiUrl, data);

    console.log('Application edited successfully:', response.data);
    toast.success('Application edited successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to edit application:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error(error.response?.data?.message || 'Invalid application data.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else if (error.response?.status === 409) {
      toast.error(error.response?.data?.message || 'Candidate already has an application for that job.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to edit application.');
    }
    throw error;
  }
};

// Get payout applications (ADMIN only)
export const getPayoutApplicationsList = async (params: {
  page?: number;
  limit?: number;
  _t?: number; // For cache busting
    search?: string;
    ledById?: string;
  jobId?: string;
  recruiterId?: string;
  joiningDateFrom?: string;
  joiningDateTo?: string;
    claimStatus?: string; 
} = {}): Promise<IGetPayoutApplicationsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params._t) queryParams.append('_t', params._t.toString());
    if (params.claimStatus) queryParams.append('claimStatus', params.claimStatus);
    if (params.search) queryParams.append('search', params.search);
    if (params.ledById) queryParams.append('ledById', params.ledById);
    if (params.jobId) queryParams.append('jobId', params.jobId);
    if (params.recruiterId) queryParams.append('recruiterId', params.recruiterId);
    if (params.joiningDateFrom) queryParams.append('joiningDateFrom', params.joiningDateFrom);
    if (params.joiningDateTo) queryParams.append('joiningDateTo', params.joiningDateTo);

    const apiUrl = `/applications/payouts/get?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching payout applications');
    console.log('API URL:', fullUrl);
    console.log('Request params:', params);

    const response = await axiosInstance.get(apiUrl);

    console.log('Payout applications fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch payout applications:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('No payout applications found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch payout applications.');
    }
    throw error;
  }
};
interface CandidateApplication {
  applicationId: string
  job: {
    id: string
    title: string
    companyName: string
  }
  appliedOn: string
  currentStatus: {
    status: string
    timestamp: string
    updatedBy: {
      id: string
      username: string
      email: string
    }
  }
  sourcedBy: {
    id: string
    username: string
    email: string
  }
}
interface ICandidateApplications{
  candidateId?: string;
  page?: number;
  limit?: number;
  data: {
    
    applications: CandidateApplication[]
  }
  totalPages: number
  totalResults: number

}

export const getCandidateApplications = async (params: {
  candidateId?: string;
  page?: number;
  limit?: number;
} = {}): Promise<ICandidateApplications> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const apiUrl = `candidates/${params.candidateId}/applications?${queryParams.toString()}`;
    const response = await axiosInstance.get(apiUrl);

    return response.data;
  } catch (error: any) {

    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('No payout applications found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch payout applications.');
    }
    throw error;
  }
};

interface IUpdateStatus {
  candidateId?: string;
  newStatus?: string;
  comments?: string;
}

export const MarkCandidateStatus = async (
  candidateId: string,
  newStatus: string,
  comments?: string
): Promise<IUpdateStatus> => {
  try {
    const apiUrl = `candidates/${candidateId}/availability`;
    const response = await axiosInstance.patch(apiUrl, {
      newStatus,
      comments,
    });

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('No payout applications found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update candidate status.');
    }
    throw error;
  }
};

// Update candidate status via status endpoint (for admin toggle)
export const updateCandidateStatus = async (
  candidateId: string,
  newStatus: boolean,
  comments?: string
): Promise<IUpdateStatus> => {
  try {
    const apiUrl = `candidates/${candidateId}/override-status`;
    const response = await axiosInstance.patch(apiUrl, {
      newStatus,
      comments,
    });

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('Candidate not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update candidate status.');
    }
    throw error;
  }
};


// Update payout details (ADMIN only)
export const updatePayoutDetails = async (
  applicationId: string,
  data: IPayoutDetails & { comments: string }
): Promise<IUpdatePayoutDetailsResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/payout-details`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Updating payout details');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    const response = await axiosInstance.patch(apiUrl, data);

    console.log('Payout details updated successfully:', response.data);
    toast.success('Payout details updated successfully');
    return response.data;
  } catch (error: any) {
    console.error('Failed to update payout details:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error('Invalid payout details data.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update payout details.');
    }
    throw error;
  }
};

// Update claim status (ADMIN only)
export const updateClaimStatus = async (
  applicationId: string,
  data: {
    isClaimed: boolean;
    comments: string;
  }
): Promise<IUpdateClaimStatusResponse> => {
  try {
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(applicationId)) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/claim-status`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Updating claim status');
    console.log('API URL:', fullUrl);
    console.log('Request Data:', data);

    const response = await axiosInstance.patch(apiUrl, data);

    console.log('Claim status updated successfully:', response.data);
    toast.success(`Commission status updated to ${data.isClaimed ? 'claimed' : 'unclaimed'}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to update claim status:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error('Invalid claim status data.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Admin permission required.');
    } else if (error.response?.status === 404) {
      toast.error('Application not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update claim status.');
    }
    throw error;
  }
};

// Get joined applications
// In your candidateapi.ts service file
export const getJoinedApplications = async (params: {
  page: number;
  limit: number;
  search?: string;
  jobId?: string;
  companyName?: string;
  recruiterId?: string;
  verificationStatus?: string;
  tenureStatus?: string;
  tenureEligibilityStatus?: string;
  claimStatus?: string;
  joiningDateFrom?: string;
  joiningDateTo?: string;
  approveStatus?: boolean;
  _t: number;
}) => {
  const query = new URLSearchParams();

  // required params
  query.append("page", params.page.toString());
  query.append("limit", params.limit.toString());

  // optional params
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && key !== "page" && key !== "limit") {
      query.append(key, String(value));
    }
  });

  return await axiosInstance.get(
    `/applications/joined-dashboard/get?${query.toString()}`
  );
};


// Get applications by candidate ID
export const getApplicationsByCandidateId = async (
  candidateId: string,
  params: {
    page?: number;
    limit?: number;
    _t?: number; // For cache busting
  } = {}
): Promise<IGetApplicationsByCandidateResponse> => {
  try {
    // Simple string validation for candidateId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!/^[0-9a-fA-F]{24}$/.test(candidateId)) {
      throw new Error('Invalid Candidate ID format.');
    }

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params._t) queryParams.append('_t', params._t.toString());

    const apiUrl = `/applications/by-candidate/${candidateId}?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching applications by candidate ID');
    console.log('API URL:', fullUrl);
    console.log('Request params:', params);

    const response = await axiosInstance.get(apiUrl);

    console.log('Applications by candidate fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch applications by candidate:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    if (error.response?.status === 400) {
      toast.error('Invalid candidate ID.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view this candidate’s applications.');
    } else if (error.response?.status === 404) {
      toast.error('Candidate or applications not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch applications by candidate.');
    }
    throw error;
  }
};

export const getCandidateById = async (candidateId: string) => {
  try {
  const response = await axiosInstance.get(`/candidates/${candidateId}`);
    return response.data;
  } catch (error: any) {
    // You can customize error handling as needed
    throw error.response?.data || error;
  }
};

export interface UpdateCandidateAdditionalDetailsForReassignPayload {
  alternativePhoneNumber?: string;
  maritalStatus?: string;
  howOftenAbleToJoin?: string;
  aadharNumber?: string;
  resumeUrl?: string;
  street?: string;
  area?: string;
  pincode?: string;
}

export const updateCandidateAdditionalDetailsForReassign = async (
  candidateId: string,
  data: UpdateCandidateAdditionalDetailsForReassignPayload
) => {
  const response = await axiosInstance.patch(
    `/candidates/${candidateId}/additional-details-for-reassign`,
    data
  );
  return response.data;
};

export interface ReassignApplicationPayload {
  newJobId: string; 
  comments: string;
}

export interface ReassignApplicationResponse {
  status: 'success';
  message: string;
  data: {
    newApplicationId: string;
    newInterviewId: string;
  };
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const reassignCandidateApplication = async (
  applicationId: string,
  payload: ReassignApplicationPayload
): Promise<ReassignApplicationResponse> => {
  try {
    const response: AxiosResponse<ReassignApplicationResponse> = await axiosInstance.post(
      `/applications/${applicationId}/reassign`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      }
    );
    return response.data;
  } catch (error: any) {
    // If token expired, try to refresh and retry once
    if (error.response?.status === 401) {
      try {
        // Always send refresh token as cookie
        const refreshResponse = await axios.post(
          `/auth/refresh-token`,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: true,
          }
        );
        const newAccessToken = refreshResponse.data?.accessToken;
        if (newAccessToken) {
          localStorage.setItem('accessToken', newAccessToken);
          // Retry the original request with new token
          const retryResponse: AxiosResponse<ReassignApplicationResponse> = await axiosInstance.post(
            `/applications/${applicationId}/reassign`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${newAccessToken}`,
              },
            }
          );
          return retryResponse.data;
        } else {
          throw new Error('Session expired. Please log in again.');
        }
      } catch (refreshError: any) {
        throw new Error(refreshError.response?.data?.message || 'Session expired. Please log in again.');
      }
    }
    throw new Error(error.response?.data?.message || 'Failed to reassign candidate application');
  }
};


// Get applications by candidate ID
export const submitApplicationFromAssignment = async (
payload:{_id:any,jobId:any}
)  => {
  try {
   
    const apiUrl = `/applications/createFromAssignement`;
   
    const response = await axiosInstance.post(apiUrl,payload);

    console.log('Applications for candidate created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch applications by candidate:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);

    throw error;
  }
};



export interface IUpdateJoiningDateResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    joiningDate: string;
    comments?: string;
  };
}

export const updateJoiningDate = async (
  payload: { joiningDate: string; applicationId: string; comments?: string }
): Promise<IUpdateJoiningDateResponse> => {
  try {
    const { applicationId, joiningDate, comments } = payload;
    // Simple string validation for applicationId (assuming MongoDB ObjectId format: 24 hex characters)
    if (!applicationId) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/joining-date`;
    console.log('Updating joining date');
    console.log('API URL:', apiUrl);
    console.log('Request Data:', { joiningDate, comments });

    const response = await axiosInstance.patch(apiUrl, { joiningDate, comments });

    console.log('Joining date updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to update joining date:', error.response?.data?.message || error.message);
    throw error;
  }
};

export interface IUpdateExpectedJoiningDateResponse {
  status: string;
  message: string;
  data: {
    applicationId: string;
    expectedJoiningDate: string;
    comments?: string;
  };
}

export const updateExpectedJoiningDate = async (
  payload: { expectedJoiningDate: string; applicationId: string; comments?: string }
): Promise<IUpdateExpectedJoiningDateResponse> => {
  try {
    const { applicationId, expectedJoiningDate, comments } = payload;
    if (!applicationId) {
      throw new Error('Invalid Application ID format.');
    }

    const apiUrl = `/applications/${applicationId}/expected-joining-date`;
    const response = await axiosInstance.patch(apiUrl, { expectedJoiningDate, comments });
    return response.data;
  } catch (error: any) {
    console.error('Failed to update expected joining date:', error.response?.data?.message || error.message);
    throw error;
  }
};